import { useCallback, useState } from "react";
import { useMedia, useUploadMedia } from "@/hooks/useMedia";
import { MediaItem } from "@/types/media";
import { Button } from "@/components/ui";

interface ImageUploaderProps {
  value: string | null;
  onChange: (url: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ImageUploader = ({ value, onChange, placeholder = "No image selected", disabled }: ImageUploaderProps) => {
  const { data: media } = useMedia(50);
  const uploadMutation = useUploadMedia();

  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;

    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith("image/"));
    if (!file) return;

    try {
      const media = await uploadMutation.mutateAsync(file);
      onChange(media.url);
    } catch (error) {
      console.error("Failed to upload file:", error);
    }
  }, [disabled, uploadMutation, onChange]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const media = await uploadMutation.mutateAsync(file);
      onChange(media.url);
    } catch (error) {
      console.error("Failed to upload file:", error);
    }

    e.target.value = "";
  }, [uploadMutation, onChange]);

  const handleSelectFromLibrary = (item: MediaItem) => {
    onChange(item.url);
    setIsLibraryOpen(false);
  };

  const handleClear = () => {
    onChange(null);
  };

  return (
    <div className="space-y-2">
      {/* Preview / Drop zone */}
      <div
        className={`relative border-2 border-dashed rounded-xl transition-colors ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-slate-200 dark:border-slate-700"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {value ? (
          <div className="relative aspect-video">
            <img
              src={value}
              alt="Selected"
              className="w-full h-full object-cover rounded-xl"
            />
            {!disabled && (
              <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={disabled || uploadMutation.isPending}
                  />
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-900 rounded-full text-sm font-medium hover:bg-slate-100 transition-colors">
                    Replace
                  </span>
                </label>
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-colors"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm text-slate-500 mb-3">
              {uploadMutation.isPending ? "Uploading..." : placeholder}
            </p>
            {!disabled && (
              <div className="flex items-center justify-center gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={disabled || uploadMutation.isPending}
                  />
                  <span
                    className="btn out sm"
                    style={uploadMutation.isPending ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                  >
                    Upload
                  </span>
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsLibraryOpen(true)}
                  disabled={uploadMutation.isPending}
                >
                  Browse Library
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Media Library Modal */}
      {isLibraryOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => setIsLibraryOpen(false)}
        >
          <div
            className="w-full max-w-3xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-800">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Select from Library</h3>
              <p className="mt-1 text-sm text-slate-500">Choose an existing image from your media library</p>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {media && media.length > 0 ? (
                <div className="grid gap-3 grid-cols-3 sm:grid-cols-4 md:grid-cols-5">
                  {media.map((item) => (
                    <button
                      key={item.mediaId}
                      type="button"
                      onClick={() => handleSelectFromLibrary(item)}
                      className={`aspect-square rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
                        value === item.url
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-transparent hover:border-slate-300 dark:hover:border-slate-600"
                      }`}
                    >
                      <img
                        src={item.url}
                        alt={item.originalFilename || item.filename}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  No images in library yet. Upload one to get started.
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsLibraryOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
